// ===========================================
// Dev Prompts Page - AI IDE Prompt Library
// ===========================================
// Console for managing, filtering, and using AI prompts
// Uses Focus → Category hierarchy for organization
// Helps developers move incredibly fast with Cursor, Copilot, etc.

import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DOMPurify from 'dompurify';  // Sanitize HTML for impact word highlighting
import { promptsAPI } from '../services/api';
import SimilarPrompts from '../components/SimilarPrompts';
import {
  Search,
  Plus,
  Copy,
  Star,
  Edit2,
  Trash2,
  Filter,
  RefreshCw,
  Loader,
  Tag,
  Clock,
  Hash,
  SquareCode,
  ChevronDown,
  ChevronRight,
  Check,
  History,
  Terminal,
  MoreVertical,
  Copy as CopyIcon,
  GripVertical,
  Zap,
  Globe,
  Lock,
  User,
  Calendar,
  Brain,      // Icon for AI-generated outcome/pattern tags
  Sparkles,   // Icon for AI-generated tags
  Wand2,      // Prompt Builder button icon
  SlidersHorizontal, // Mobile filter trigger icon
  X           // Close button icon
} from 'lucide-react';
import {
  CATEGORIES,
  ROLE_CATEGORIES,
  ACTION_CATEGORIES
} from '../data/promptCategories';
import PromptModal from '../components/PromptModal';
import PromptRevisionModal from '../components/PromptRevisionModal';
import TagFilter from '../components/TagFilter';
import './DevPrompts.scss';

// ===========================================
// Target Agent Configuration
// ===========================================
const AGENTS = {
  universal: { label: 'Universal', color: 'var(--color-text-muted)' },
  cursor: { label: 'Cursor', color: '#00d4ff' },
  copilot: { label: 'Copilot', color: '#6366f1' },
  claude: { label: 'Claude', color: '#d97706' },
  chatgpt: { label: 'ChatGPT', color: '#10a37f' }
};

// ===========================================
// Markdown Parser for Impact Word Highlighting
// ===========================================
// Parses markdown syntax (**bold**, `code`) to highlight impact words
// in prompt content, similar to Note Builder styling
const parsePromptContent = (content) => {
  if (!content) return '';
  
  // Parse markdown: **bold** for impact words, `code` for inline code
  // Escape HTML first, then apply markdown transforms
  const escaped = content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  return escaped
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')  // **impact words** → highlighted
    .replace(/`([^`]+)`/g, '<code>$1</code>')         // `code` → code styling
    .replace(/\n/g, '<br>');                          // Preserve line breaks
};

// ===========================================
// Sort Options
// ===========================================
const SORT_OPTIONS = [
  { value: 'custom', label: 'Custom Order' },
  { value: 'recent', label: 'Recently Updated' },
  { value: 'newest', label: 'Newest Created' },
  { value: 'oldest', label: 'Oldest Created' },
  { value: 'popular', label: 'Most Used' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'alpha', label: 'Alphabetical' },
  { value: 'favorites', label: 'Favorites First' }
];

const DevPrompts = () => {
  // ===========================================
  // State Management
  // ===========================================
  const [prompts, setPrompts] = useState([]);
  const [stats, setStats] = useState({ totalPrompts: 0, totalUses: 0, favoriteCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');     // Debounced search to prevent API spam
  const [selectedRole, setSelectedRole] = useState('all');        // Legacy: engineering role filter
  const [selectedAction, setSelectedAction] = useState('all');    // Legacy: action type filter
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [sortBy, setSortBy] = useState('custom');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  // ===========================================
  // AI Tag Category Filters - Dynamic & Organic
  // ===========================================
  // These categories grow as users create prompts and AI generates tags
  const [aiTagCategories, setAiTagCategories] = useState({
    outcomes: [],    // What prompts help accomplish (e.g., "api-integration", "error-handling")
    patterns: [],    // Development approaches (e.g., "clean-architecture", "tdd")
    technologies: [] // Tech stack items (e.g., "react", "mongodb", "typescript")
  });
  const [selectedOutcome, setSelectedOutcome] = useState(null);   // Filter by outcome tag
  const [selectedPattern, setSelectedPattern] = useState(null);   // Filter by pattern tag
  const [selectedTech, setSelectedTech] = useState(null);         // Filter by technology tag
  
  // Sidebar section collapse state for AI tag categories
  const [outcomesCollapsed, setOutcomesCollapsed] = useState(false);
  const [patternsCollapsed, setPatternsCollapsed] = useState(true);
  const [techCollapsed, setTechCollapsed] = useState(true);
  
  // Tag-based filtering (auto-detected tech tags)
  const [selectedTags, setSelectedTags] = useState([]);
  
  // Star rating filter (minimum rating 1-5, 0 = show all)
  const [minRating, setMinRating] = useState(0);
  
  // Visibility filter - 'all', 'mine', 'public', 'private'
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  
  // Clipboard feedback
  const [copiedId, setCopiedId] = useState(null);
  
  // Version history modal
  const [showVersions, setShowVersions] = useState(false);
  const [versionPrompt, setVersionPrompt] = useState(null);
  const [versions, setVersions] = useState([]);
  
  // Compact/Expanded card state - tracks which cards are expanded
  const [expandedIds, setExpandedIds] = useState(new Set());
  
  // Prompt Revision Modal state - for quick prompt iteration
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionPrompt, setRevisionPrompt] = useState(null);
  
  // Mobile filter sheet state
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // ===========================================
  // Click-outside handlers for dropdowns
  // ===========================================
  // Close sort dropdown when clicking outside
  useEffect(() => {
    if (!showSortDropdown) return;
    
    const handleClickOutside = (e) => {
      // Check if click is outside the sort dropdown area
      if (!e.target.closest('.dev-prompts__sort')) {
        setShowSortDropdown(false);
      }
    };
    
    // Use setTimeout to avoid immediate trigger from the click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showSortDropdown]);

  // ===========================================
  // Debounce Search Query
  // ===========================================
  // Prevents API call on every keystroke - waits 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);  // 300ms debounce delay
    return () => clearTimeout(timer);
  }, [searchQuery]);


  // ===========================================
  // Fetch Prompts
  // ===========================================
  // Wrapped in useCallback with filter dependencies for proper memoization
  // Uses debouncedSearch to prevent API spam on every keystroke
  // Uses hybrid search (full-text + vector) when search query is present
  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // ===========================================
      // Hybrid Search Mode (when search query present)
      // ===========================================
      // Uses dedicated search endpoint with full-text + vector search
      // Backend requires minimum 2 characters for search
      if (debouncedSearch.trim().length >= 2) {
        const response = await promptsAPI.search(debouncedSearch.trim(), { limit: 50 });
        
        if (response.data.success) {
          // Hybrid search returns { results, meta } structure
          setPrompts(response.data.data.results || []);
          // Note: Stats not included in search results, keep existing stats
        }
        setLoading(false);
        return;
      }
      
      // ===========================================
      // Standard List Mode (browsing with filters)
      // ===========================================
      // Build query params from current filter state
      const params = { sort: sortBy };
      
      // Role filter (primary category) - filters by engineering perspective
      if (selectedRole !== 'all') {
        params.role = selectedRole;
      }
      // Action filter (secondary category) - filters by task type
      if (selectedAction !== 'all') {
        params.action = selectedAction;
      }
      // Favorites filter
      if (showFavoritesOnly) {
        params.favorite = 'true';
      }
      // Tag-based filtering (comma-separated for backend)
      if (selectedTags.length > 0) {
        params.tags = selectedTags.join(',');
      }
      // Rating filter (minimum rating)
      if (minRating > 0) {
        params.minRating = minRating;
      }
      // Visibility filter (all, mine, public, private)
      if (visibilityFilter !== 'all') {
        params.visibility = visibilityFilter;
      }
      
      // ===========================================
      // AI Tag Category Filters (Dynamic Categories)
      // ===========================================
      // Filter by AI-generated semantic tags
      if (selectedOutcome) {
        params.outcomeTag = selectedOutcome;
      }
      if (selectedPattern) {
        params.patternTag = selectedPattern;
      }
      if (selectedTech) {
        params.techTag = selectedTech;
      }
      
      const response = await promptsAPI.list(params);
      
      if (response.data.success) {
        setPrompts(response.data.data.prompts || []);
        setStats(response.data.data.stats || { totalPrompts: 0, totalUses: 0, favoriteCount: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch prompts:', err);
      setError('Failed to load prompts. Please try again.');
    }
    
    setLoading(false);
  }, [selectedRole, selectedAction, showFavoritesOnly, sortBy, debouncedSearch, selectedTags, minRating, visibilityFilter, selectedOutcome, selectedPattern, selectedTech]);

  // Fetch on mount and when fetchPrompts changes (filter state changes)
  // fetchPrompts is memoized with useCallback, so it only changes when filters change
  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // ===========================================
  // Fetch AI Tag Categories for Sidebar
  // ===========================================
  // These categories grow organically as users create prompts
  const fetchAITagCategories = async () => {
    try {
      const response = await promptsAPI.getAITagCategories();
      if (response.data.success) {
        setAiTagCategories(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch AI tag categories:', err);
    }
  };
  
  // Fetch on mount
  useEffect(() => {
    fetchAITagCategories();
  }, []);

  // ===========================================
  // Handlers
  // ===========================================
  
  // Copy prompt to clipboard
  const handleCopy = async (prompt) => {
    try {
      await navigator.clipboard.writeText(prompt.content);
      setCopiedId(prompt._id);
      
      // Record the copy action for analytics
      await promptsAPI.recordCopy(prompt._id);
      
      // Update local state
      setPrompts(prev => prev.map(p => 
        p._id === prompt._id 
          ? { ...p, useCount: p.useCount + 1 } 
          : p
      ));
      
      // Clear copied indicator after 2 seconds
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // ===========================================
  // Open Prompt Revision Modal
  // ===========================================
  // Opens the quick revision modal for fast prompt iteration
  // Simple 2-step flow: Intent → Review & Save
  const handleOpenRevisionModal = (prompt) => {
    setRevisionPrompt(prompt);
    setShowRevisionModal(true);
  };

  // Handle save from Revision Modal - opens PromptModal with revised content
  const handleRevisionSave = (revisedPrompt) => {
    // Close revision modal and open save modal with the revised prompt
    setShowRevisionModal(false);
    setRevisionPrompt(null);
    setEditingPrompt(revisedPrompt);
    setShowModal(true);
  };

  // Toggle favorite
  const handleToggleFavorite = async (prompt) => {
    try {
      await promptsAPI.toggleFavorite(prompt._id);
      
      // Update local state
      setPrompts(prev => prev.map(p => 
        p._id === prompt._id 
          ? { ...p, isFavorite: !p.isFavorite } 
          : p
      ));
      
      // Update stats
      setStats(prev => ({
        ...prev,
        favoriteCount: prompt.isFavorite ? prev.favoriteCount - 1 : prev.favoriteCount + 1
      }));
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  // Set star rating (1-5, 0 to clear)
  const handleSetRating = async (prompt, rating) => {
    try {
      // Use the update endpoint to set rating
      await promptsAPI.update(prompt._id, { rating });
      
      // Update local state
      setPrompts(prev => prev.map(p => 
        p._id === prompt._id 
          ? { ...p, rating } 
          : p
      ));
    } catch (err) {
      console.error('Failed to set rating:', err);
    }
  };

  // Open create modal
  const handleCreate = () => {
    setEditingPrompt(null);
    setShowModal(true);
  };

  // Open edit modal
  const handleEdit = (prompt) => {
    setEditingPrompt(prompt);
    setShowModal(true);
  };

  // Delete prompt
  const handleDelete = async (prompt) => {
    if (!window.confirm(`Delete "${prompt.title}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await promptsAPI.delete(prompt._id);
      setPrompts(prev => prev.filter(p => p._id !== prompt._id));
      setStats(prev => ({
        ...prev,
        totalPrompts: prev.totalPrompts - 1,
        favoriteCount: prompt.isFavorite ? prev.favoriteCount - 1 : prev.favoriteCount
      }));
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  // Duplicate prompt
  const handleDuplicate = async (prompt) => {
    try {
      const response = await promptsAPI.duplicate(prompt._id);
      if (response.data.success) {
        // Add to list and refetch to get updated stats
        fetchPrompts();
      }
    } catch (err) {
      console.error('Failed to duplicate:', err);
    }
  };

  // Save prompt (create or update)
  const handleSave = async (promptData) => {
    try {
      if (editingPrompt) {
        // Update existing
        const response = await promptsAPI.update(editingPrompt._id, promptData);
        if (response.data.success) {
          setPrompts(prev => prev.map(p => 
            p._id === editingPrompt._id ? response.data.data : p
          ));
        }
      } else {
        // Create new
        const response = await promptsAPI.create(promptData);
        if (response.data.success) {
          setPrompts(prev => [response.data.data, ...prev]);
          setStats(prev => ({ ...prev, totalPrompts: prev.totalPrompts + 1 }));
        }
      }
      
      setShowModal(false);
      setEditingPrompt(null);
      
      // Refresh AI tag categories after save - new tags may have been generated
      // Small delay to allow backend AI tag generation to complete
      setTimeout(fetchAITagCategories, 2000);
      
      return { success: true };
    } catch (err) {
      console.error('Failed to save:', err);
      return { success: false, error: err.message };
    }
  };

  // View version history
  const handleViewVersions = async (prompt) => {
    try {
      const response = await promptsAPI.getVersions(prompt._id);
      if (response.data.success) {
        setVersionPrompt(prompt);
        setVersions(response.data.data.versions || []);
        setShowVersions(true);
      }
    } catch (err) {
      console.error('Failed to get versions:', err);
    }
  };

  // Handle drag and drop reordering
  const handleDragEnd = async (result) => {
    // Dropped outside the list
    if (!result.destination) return;
    
    // No change in position
    if (result.destination.index === result.source.index) return;
    
    // Reorder locally first for instant feedback
    const reorderedPrompts = Array.from(prompts);
    const [movedPrompt] = reorderedPrompts.splice(result.source.index, 1);
    reorderedPrompts.splice(result.destination.index, 0, movedPrompt);
    
    setPrompts(reorderedPrompts);
    
    // Persist the new order to the backend
    try {
      const orderedIds = reorderedPrompts.map(p => p._id);
      await promptsAPI.reorder(orderedIds);
    } catch (err) {
      console.error('Failed to save order:', err);
      // Revert on error
      fetchPrompts();
    }
  };

  // Toggle card expanded/collapsed state
  const toggleExpanded = (promptId) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(promptId)) {
        newSet.delete(promptId);
      } else {
        newSet.add(promptId);
      }
      return newSet;
    });
  };

  // Restore a version
  const handleRestoreVersion = async (version) => {
    if (!versionPrompt) return;
    
    try {
      const response = await promptsAPI.restoreVersion(versionPrompt._id, version.version);
      if (response.data.success) {
        // Update the prompt in the list
        setPrompts(prev => prev.map(p => 
          p._id === versionPrompt._id ? response.data.data : p
        ));
        setShowVersions(false);
        setVersionPrompt(null);
      }
    } catch (err) {
      console.error('Failed to restore version:', err);
    }
  };

  // ===========================================
  // Render Helpers
  // ===========================================
  
  // Get role info from the role categories
  // Handles undefined/null roleKey gracefully (for prompts without role set)
  const getRoleInfo = (roleKey) => {
    // Handle undefined or null roleKey
    if (!roleKey) {
      return {
        key: 'general',
        label: 'General',
        icon: SquareCode,
        color: 'var(--color-text-muted)'
      };
    }
    if (ROLE_CATEGORIES[roleKey]) {
      return ROLE_CATEGORIES[roleKey];
    }
    // Fallback for unknown roles
    return {
      key: roleKey,
      label: roleKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      icon: SquareCode,
      color: 'var(--color-text-muted)'
    };
  };

  // Get action info from the action categories
  // Handles undefined/null actionKey gracefully
  const getActionInfo = (actionKey) => {
    // Handle undefined or null actionKey
    if (!actionKey) {
      return {
        key: 'generate',
        label: 'Generate',
        icon: SquareCode,
        color: 'var(--color-text-muted)'
      };
    }
    if (ACTION_CATEGORIES[actionKey]) {
      return ACTION_CATEGORIES[actionKey];
    }
    // Fallback for unknown actions
    return {
      key: actionKey,
      label: actionKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      icon: SquareCode,
      color: 'var(--color-text-muted)'
    };
  };

  // Legacy: Get category info (for backwards compatibility)
  // Note: Kept for potential future use with legacy prompts that use category field
  // Handles undefined/null categoryKey gracefully
  // eslint-disable-next-line no-unused-vars
  const getCategoryInfo = (categoryKey) => {
    // Handle undefined or null categoryKey
    if (!categoryKey) {
      return {
        key: 'general',
        label: 'General',
        icon: SquareCode,
        color: 'var(--color-text-muted)'
      };
    }
    if (CATEGORIES[categoryKey]) {
      return CATEGORIES[categoryKey];
    }
    return {
      key: categoryKey,
      label: categoryKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      icon: SquareCode,
      color: 'var(--color-text-muted)'
    };
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Truncate text
  const truncate = (text, length = 150) => {
    if (!text || text.length <= length) return text;
    return text.substring(0, length) + '...';
  };

  // ===========================================
  // Mobile Filter Helpers
  // ===========================================
  // Count active filters for the mobile filter badge
  const getActiveFilterCount = () => {
    let count = 0;
    if (showFavoritesOnly) count++;
    if (visibilityFilter !== 'all') count++;
    if (selectedOutcome) count++;
    if (selectedPattern) count++;
    if (selectedTech) count++;
    if (selectedTags.length > 0) count += selectedTags.length;
    if (minRating > 0) count++;
    return count;
  };

  // Clear all filters
  const clearAllFilters = () => {
    setShowFavoritesOnly(false);
    setVisibilityFilter('all');
    setSelectedRole('all');
    setSelectedAction('all');
    setSelectedOutcome(null);
    setSelectedPattern(null);
    setSelectedTech(null);
    setSelectedTags([]);
    setMinRating(0);
  };

  // ===========================================
  // Loading State
  // ===========================================
  if (loading && prompts.length === 0) {
    return (
      <div className="dev-prompts dev-prompts--loading">
        <Loader className="animate-spin" size={32} />
        <p>Loading prompts...</p>
      </div>
    );
  }

  // ===========================================
  // Render
  // ===========================================
  return (
    <div className="dev-prompts">
      {/* Header */}
      <div className="dev-prompts__header">
        <div className="dev-prompts__header-info">
          <h1>Dev Prompts</h1>
          <p>Your AI agent toolkit for moving fast</p>
        </div>
        <div className="dev-prompts__header-actions">
          <button className="btn-secondary" onClick={fetchPrompts} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button className="btn-primary" onClick={handleCreate}>
            <Plus size={16} />
            New Prompt
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="dev-prompts__stats">
        <div className="dev-prompts__stat">
          <Terminal size={16} />
          <span className="dev-prompts__stat-value">{stats.totalPrompts}</span>
          <span className="dev-prompts__stat-label">Prompts</span>
        </div>
        <div className="dev-prompts__stat">
          <Copy size={16} />
          <span className="dev-prompts__stat-value">{stats.totalUses}</span>
          <span className="dev-prompts__stat-label">Total Uses</span>
        </div>
        <div className="dev-prompts__stat">
          <Star size={16} />
          <span className="dev-prompts__stat-value">{stats.favoriteCount}</span>
          <span className="dev-prompts__stat-label">Favorites</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="dev-prompts__content">
        {/* Sidebar - Focus → Category hierarchy */}
        <aside className="dev-prompts__sidebar">
          <div className="dev-prompts__sidebar-section">
            <h3>Filter</h3>
            <ul className="dev-prompts__category-list">
              {/* Favorites toggle */}
              <li>
                <button 
                  className={`dev-prompts__category-btn ${showFavoritesOnly ? 'active' : ''}`}
                  onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                >
                  <Star size={16} />
                  <span>Favorites</span>
                  {stats.favoriteCount > 0 && (
                    <span className="dev-prompts__category-count">{stats.favoriteCount}</span>
                  )}
                </button>
              </li>
              
              {/* All Prompts - clears all category filters */}
              <li>
                <button
                  className={`dev-prompts__category-btn ${!selectedOutcome && !selectedPattern && !selectedTech && selectedRole === 'all' && selectedAction === 'all' ? 'active' : ''}`}
                  onClick={() => { 
                    // Clear legacy role/action filters
                    setSelectedRole('all'); 
                    setSelectedAction('all');
                    // Clear AI tag category filters
                    setSelectedOutcome(null);
                    setSelectedPattern(null);
                    setSelectedTech(null);
                  }}
                >
                  <SquareCode size={16} style={{ color: !selectedOutcome && !selectedPattern && !selectedTech ? 'var(--color-accent)' : undefined }} />
                  <span>All Prompts</span>
                </button>
              </li>

              {/* Visibility Group Header */}
              <li className="dev-prompts__category-group">
                <span className="dev-prompts__category-group-label">Visibility</span>
              </li>
              
              {/* My Prompts */}
              <li>
                <button
                  className={`dev-prompts__category-btn ${visibilityFilter === 'mine' ? 'active' : ''}`}
                  onClick={() => setVisibilityFilter(visibilityFilter === 'mine' ? 'all' : 'mine')}
                >
                  <User size={16} style={{ color: visibilityFilter === 'mine' ? '#10b981' : undefined }} />
                  <span>My Prompts</span>
                </button>
              </li>
              
              {/* Public Prompts */}
              <li>
                <button
                  className={`dev-prompts__category-btn ${visibilityFilter === 'public' ? 'active' : ''}`}
                  onClick={() => setVisibilityFilter(visibilityFilter === 'public' ? 'all' : 'public')}
                >
                  <Globe size={16} style={{ color: visibilityFilter === 'public' ? '#10b981' : undefined }} />
                  <span>Public</span>
                </button>
              </li>
              
              {/* Private Prompts */}
              <li>
                <button
                  className={`dev-prompts__category-btn ${visibilityFilter === 'private' ? 'active' : ''}`}
                  onClick={() => setVisibilityFilter(visibilityFilter === 'private' ? 'all' : 'private')}
                >
                  <Lock size={16} style={{ color: visibilityFilter === 'private' ? 'var(--color-text-muted)' : undefined }} />
                  <span>Private</span>
                </button>
              </li>

              {/* ===========================================
                  AI TAG CATEGORIES - Dynamic & Organic Filtering
                  Categories grow as users create prompts & AI tags them
              =========================================== */}
              
              {/* OUTCOMES - What prompts help accomplish */}
              <li className="dev-prompts__category-group dev-prompts__category-group--collapsible">
                <button 
                  className="dev-prompts__category-group-toggle"
                  onClick={() => setOutcomesCollapsed(!outcomesCollapsed)}
                >
                  {outcomesCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                  <Sparkles size={12} style={{ color: '#ec4899', marginRight: '4px' }} />
                  <span className="dev-prompts__category-group-label">Outcomes</span>
                  {aiTagCategories.outcomes.length > 0 && (
                    <span className="dev-prompts__category-group-count">
                      {aiTagCategories.outcomes.length}
                    </span>
                  )}
                </button>
              </li>
              {!outcomesCollapsed && aiTagCategories.outcomes.map(({ tag, count }) => {
                const isSelected = selectedOutcome === tag;
                
                return (
                  <li key={`outcome-${tag}`} className="dev-prompts__category-item">
                    <button
                      className={`dev-prompts__category-btn dev-prompts__category-btn--ai ${isSelected ? 'active' : ''}`}
                      onClick={() => setSelectedOutcome(isSelected ? null : tag)}
                      title={`Filter by outcome: ${tag}`}
                    >
                      <Sparkles 
                        size={12} 
                        style={{ color: isSelected ? '#ec4899' : 'var(--color-text-muted)' }} 
                      />
                      <span>{tag}</span>
                      <span className="dev-prompts__category-count">{count}</span>
                    </button>
                  </li>
                );
              })}
              {!outcomesCollapsed && aiTagCategories.outcomes.length === 0 && (
                <li className="dev-prompts__category-empty">
                  <span>No outcomes yet</span>
                </li>
              )}

              {/* PATTERNS - Development approaches & methodologies */}
              <li className="dev-prompts__category-group dev-prompts__category-group--collapsible">
                <button 
                  className="dev-prompts__category-group-toggle"
                  onClick={() => setPatternsCollapsed(!patternsCollapsed)}
                >
                  {patternsCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                  <Brain size={12} style={{ color: '#a855f7', marginRight: '4px' }} />
                  <span className="dev-prompts__category-group-label">Patterns</span>
                  {aiTagCategories.patterns.length > 0 && (
                    <span className="dev-prompts__category-group-count">
                      {aiTagCategories.patterns.length}
                    </span>
                  )}
                </button>
              </li>
              {!patternsCollapsed && aiTagCategories.patterns.map(({ tag, count }) => {
                const isSelected = selectedPattern === tag;
                
                return (
                  <li key={`pattern-${tag}`} className="dev-prompts__category-item">
                    <button
                      className={`dev-prompts__category-btn dev-prompts__category-btn--ai ${isSelected ? 'active' : ''}`}
                      onClick={() => setSelectedPattern(isSelected ? null : tag)}
                      title={`Filter by pattern: ${tag}`}
                    >
                      <Brain 
                        size={12} 
                        style={{ color: isSelected ? '#a855f7' : 'var(--color-text-muted)' }} 
                      />
                      <span>{tag}</span>
                      <span className="dev-prompts__category-count">{count}</span>
                    </button>
                  </li>
                );
              })}
              {!patternsCollapsed && aiTagCategories.patterns.length === 0 && (
                <li className="dev-prompts__category-empty">
                  <span>No patterns yet</span>
                </li>
              )}

              {/* TECH - Technologies detected in prompts */}
              <li className="dev-prompts__category-group dev-prompts__category-group--collapsible">
                <button 
                  className="dev-prompts__category-group-toggle"
                  onClick={() => setTechCollapsed(!techCollapsed)}
                >
                  {techCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                  <Zap size={12} style={{ color: '#00d4ff', marginRight: '4px' }} />
                  <span className="dev-prompts__category-group-label">Tech</span>
                  {aiTagCategories.technologies.length > 0 && (
                    <span className="dev-prompts__category-group-count">
                      {aiTagCategories.technologies.length}
                    </span>
                  )}
                </button>
              </li>
              {!techCollapsed && aiTagCategories.technologies.map(({ tag, count }) => {
                const isSelected = selectedTech === tag;
                
                return (
                  <li key={`tech-${tag}`} className="dev-prompts__category-item">
                    <button
                      className={`dev-prompts__category-btn dev-prompts__category-btn--ai ${isSelected ? 'active' : ''}`}
                      onClick={() => setSelectedTech(isSelected ? null : tag)}
                      title={`Filter by technology: ${tag}`}
                    >
                      <Zap 
                        size={12} 
                        style={{ color: isSelected ? '#00d4ff' : 'var(--color-text-muted)' }} 
                      />
                      <span>{tag}</span>
                      <span className="dev-prompts__category-count">{count}</span>
                    </button>
                  </li>
                );
              })}
              {!techCollapsed && aiTagCategories.technologies.length === 0 && (
                <li className="dev-prompts__category-empty">
                  <span>No tech tags yet</span>
                </li>
              )}
            </ul>
          </div>
        </aside>

        {/* Main Panel - Prompt List */}
        <main className="dev-prompts__main">
          {/* Search & Sort Bar */}
          <div className="dev-prompts__toolbar">
            <div className="dev-prompts__search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            {/* Tag Filter - Auto-detected technology tags */}
            <TagFilter
              selectedTags={selectedTags}
              onTagsChange={setSelectedTags}
              compact
            />
            
            {/* Rating Filter - Filter by minimum star rating */}
            <div className="dev-prompts__rating-filter">
              <button 
                className={`dev-prompts__rating-filter-btn ${minRating > 0 ? 'active' : ''}`}
                onClick={() => setMinRating(minRating > 0 ? 0 : 3)}
                title={minRating > 0 ? 'Clear rating filter' : 'Filter by rating'}
              >
                <Star size={14} fill={minRating > 0 ? 'currentColor' : 'none'} />
                {minRating > 0 ? `${minRating}+` : ''}
              </button>
              {minRating > 0 && (
                <div className="dev-prompts__rating-filter-dropdown">
                  {[1, 2, 3, 4, 5].map((r) => (
                    <button
                      key={r}
                      className={minRating === r ? 'active' : ''}
                      onClick={() => setMinRating(r)}
                    >
                      {r}+ <Star size={12} fill="currentColor" />
                    </button>
                  ))}
                  <button onClick={() => setMinRating(0)}>Clear</button>
                </div>
              )}
            </div>
            
            {/* Sort Dropdown */}
            <div className="dev-prompts__sort">
              <button 
                className="dev-prompts__sort-btn"
                onClick={() => setShowSortDropdown(!showSortDropdown)}
              >
                <Filter size={14} />
                {SORT_OPTIONS.find(o => o.value === sortBy)?.label}
                <ChevronDown size={14} />
              </button>
              
              {showSortDropdown && (
                <div className="dev-prompts__sort-dropdown">
                  {SORT_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      className={sortBy === option.value ? 'active' : ''}
                      onClick={() => {
                        setSortBy(option.value);
                        setShowSortDropdown(false);
                      }}
                    >
                      {sortBy === option.value && <Check size={14} />}
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="dev-prompts__error">
              <p>{error}</p>
              <button onClick={fetchPrompts}>Try Again</button>
            </div>
          )}

          {/* Prompt Cards with Drag & Drop */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="prompts-list">
              {(provided, snapshot) => (
                <div 
                  className={`dev-prompts__list ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {prompts.length === 0 ? (
                    <div className="dev-prompts__empty">
                      <SquareCode size={48} />
                      <h3>No prompts yet</h3>
                      <p>Create your first AI prompt to start building your toolkit</p>
                      <button className="btn-primary" onClick={handleCreate}>
                        <Plus size={16} />
                        Create First Prompt
                      </button>
                    </div>
                  ) : (
                    prompts.map((prompt, index) => {
                      // Get role info (primary) - use prompt.role or fall back to category for backwards compatibility
                      const roleInfo = getRoleInfo(prompt.role || prompt.category);
                      const RoleIcon = roleInfo.icon;
                      // Get action info (secondary)
                      const actionInfo = getActionInfo(prompt.action || 'generate');
                      
                      return (
                        <Draggable key={prompt._id} draggableId={prompt._id} index={index}>
                          {(provided, snapshot) => {
                            // Check if this card is expanded
                            const isExpanded = expandedIds.has(prompt._id);
                            
                            return (
                              <div 
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`dev-prompts__card ${snapshot.isDragging ? 'dragging' : ''} ${isExpanded ? 'expanded' : 'compact'}`}
                                onClick={() => toggleExpanded(prompt._id)}
                                data-prompt-id={prompt._id}
                              >
                                {/* Card Header - Always visible in compact mode */}
                                <div className="dev-prompts__card-header">
                                  {/* Expand indicator */}
                                  <div className={`dev-prompts__expand-icon ${isExpanded ? 'expanded' : ''}`}>
                                    <ChevronRight size={14} />
                                  </div>
                                  
                                  {/* Drag Handle - only show in custom sort mode */}
                                  {sortBy === 'custom' && (
                                    <div 
                                      className="dev-prompts__drag-handle"
                                      {...provided.dragHandleProps}
                                      onClick={(e) => e.stopPropagation()}
                                      title="Drag to reorder"
                                    >
                                      <GripVertical size={14} />
                                    </div>
                                  )}
                                  
                                  {/* Role icon */}
                                  <div className="dev-prompts__card-icon" style={{ borderColor: roleInfo.color }}>
                                    <RoleIcon size={14} style={{ color: roleInfo.color }} />
                                  </div>
                                  
                                  {/* Title area */}
                                  <div className="dev-prompts__card-title-area">
                                    <h4 className="dev-prompts__card-title">{prompt.title}</h4>
                                    {/* Compact mode: show inline meta */}
                                    {!isExpanded && (
                                      <span className="dev-prompts__card-inline-meta">
                                        <span className="dev-prompts__card-role-inline" style={{ color: roleInfo.color }}>
                                          {roleInfo.label}
                                        </span>
                                        <span className="dev-prompts__card-action-inline" style={{ color: actionInfo.color }}>
                                          {actionInfo.label}
                                        </span>
                                        <span className="dev-prompts__card-uses">
                                          <Copy size={10} /> {prompt.useCount || 0}
                                        </span>
                                        {prompt.tags && prompt.tags.length > 0 && (
                                          <span className="dev-prompts__card-tag-count">
                                            <Hash size={10} /> {prompt.tags.length}
                                          </span>
                                        )}
                                        {/* Star rating - compact display */}
                                        {prompt.rating > 0 && (
                                          <span className="dev-prompts__card-rating-compact">
                                            <Star size={10} fill="currentColor" /> {prompt.rating}
                                          </span>
                                        )}
                                        {/* Visibility indicator - public/private */}
                                        <span className={`dev-prompts__card-visibility ${prompt.isPublic ? 'dev-prompts__card-visibility--public' : 'dev-prompts__card-visibility--private'}`}>
                                          {prompt.isPublic ? <Globe size={10} /> : <Lock size={10} />}
                                        </span>
                                        {/* Prompt Builder indicator - neon yellow badge */}
                                        {prompt.createdByAI && (
                                          <span className="dev-prompts__card-ai-badge">
                                            AI
                                          </span>
                                        )}
                                        {/* Created by attribution */}
                                        {prompt.createdBy && (
                                          <span className="dev-prompts__card-created-by">
                                            Created by: {prompt.createdBy}
                                          </span>
                                        )}
                                      </span>
                                    )}
                                    {/* Expanded mode: show full meta */}
                                    {isExpanded && (
                                      <div className="dev-prompts__card-meta">
                                        <span className="dev-prompts__card-role" style={{ color: roleInfo.color }}>
                                          {roleInfo.label}
                                        </span>
                                        <span className="dev-prompts__card-action" style={{ color: actionInfo.color }}>
                                          {actionInfo.label}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Actions - stop propagation to prevent card toggle */}
                                  <div className="dev-prompts__card-actions" onClick={(e) => e.stopPropagation()}>
                                    {/* Quick Revision - fast prompt iteration */}
                                    <button 
                                      className="dev-prompts__action-btn dev-prompts__action-btn--builder"
                                      onClick={() => handleOpenRevisionModal(prompt)}
                                      title="Revise prompt & save new version"
                                    >
                                      <Wand2 size={18} />
                                    </button>
                                    <button 
                                      className={`dev-prompts__action-btn dev-prompts__action-btn--favorite ${prompt.isFavorite ? 'favorite' : ''}`}
                                      onClick={() => handleToggleFavorite(prompt)}
                                      title={prompt.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                                    >
                                      {prompt.isFavorite ? <Star size={18} fill="currentColor" /> : <Star size={18} />}
                                    </button>
                                    <button
                                      className={`dev-prompts__copy-btn ${copiedId === prompt._id ? 'copied' : ''}`}
                                      onClick={() => handleCopy(prompt)}
                                      title="Copy prompt"
                                    >
                                      {copiedId === prompt._id ? <Check size={16} /> : <Copy size={16} />}
                                    </button>
                                    <div className="dev-prompts__action-menu">
                                      <button className="dev-prompts__action-btn dev-prompts__action-btn--menu" title="More actions">
                                        <MoreVertical size={18} />
                                      </button>
                                      <div className="dev-prompts__action-dropdown">
                                        <button onClick={() => handleEdit(prompt)}>
                                          <Edit2 size={16} />
                                          Edit
                                        </button>
                                        <button onClick={() => handleDuplicate(prompt)}>
                                          <CopyIcon size={16} />
                                          Duplicate
                                        </button>
                                        <button onClick={() => handleViewVersions(prompt)}>
                                          <History size={16} />
                                          Version History
                                        </button>
                                        <button className="danger" onClick={() => handleDelete(prompt)}>
                                          <Trash2 size={16} />
                                          Delete
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Expanded Content - Only shown when expanded */}
                                {isExpanded && (
                                  <div className="dev-prompts__card-expanded">
                                    {/* Card Description */}
                                    {prompt.description && (
                                      <p className="dev-prompts__card-description">{prompt.description}</p>
                                    )}

                                    {/* Card Content - Full content when expanded */}
                                    {/* Supports **impact words** and `code` markdown syntax */}
                                    <div className="dev-prompts__card-content">
                                      <code dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                          parsePromptContent(prompt.content),
                                          { ALLOWED_TAGS: ['strong', 'code', 'br'] }
                                        )
                                      }} />
                                    </div>

                                    {/* Tags - Shows user, AI, and auto-detected tags */}
                                    {prompt.tags && prompt.tags.length > 0 && (
                                      <div className="dev-prompts__card-tags">
                                        {prompt.tags.map(tag => {  // Show all tags when expanded
                                          // Determine tag type for styling
                                          // Priority: AI tags > Auto tags > Manual tags
                                          const isAITag = prompt.aiTags?.includes(tag);
                                          const isAutoTag = prompt.autoTags?.includes(tag);
                                          const isUserTag = prompt.manualTags?.includes(tag);
                                          
                                          // Determine specific AI tag category for icons
                                          const isOutcome = prompt.aiTagCategories?.outcomes?.includes(tag);
                                          const isPattern = prompt.aiTagCategories?.patterns?.includes(tag);
                                          
                                          // Get appropriate class and icon
                                          let tagClass = 'dev-prompts__tag';
                                          let TagIcon = Hash;
                                          let tagTitle = 'User tag';
                                          
                                          if (isAITag) {
                                            if (isOutcome) {
                                              tagClass += ' dev-prompts__tag--ai-outcome';
                                              TagIcon = Sparkles;
                                              tagTitle = 'AI tag: Outcome';
                                            } else if (isPattern) {
                                              tagClass += ' dev-prompts__tag--ai-pattern';
                                              TagIcon = Brain;
                                              tagTitle = 'AI tag: Pattern';
                                            } else {
                                              tagClass += ' dev-prompts__tag--ai-tech';
                                              TagIcon = Zap;
                                              tagTitle = 'AI tag: Technology';
                                            }
                                          } else if (isAutoTag) {
                                            tagClass += ' dev-prompts__tag--auto';
                                            TagIcon = Zap;
                                            tagTitle = 'Auto-detected technology';
                                          } else if (isUserTag) {
                                            tagClass += ' dev-prompts__tag--user';
                                            TagIcon = Hash;
                                            tagTitle = 'User tag';
                                          }
                                          
                                          return (
                                            <span 
                                              key={tag} 
                                              className={tagClass}
                                              title={tagTitle}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // Click tag to filter by it
                                                if (!selectedTags.includes(tag)) {
                                                  setSelectedTags([...selectedTags, tag]);
                                                }
                                              }}
                                            >
                                              <TagIcon size={9} />
                                              {tag}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {/* Star Rating - Clickable 1-5 stars */}
                                    <div className="dev-prompts__card-rating" onClick={(e) => e.stopPropagation()}>
                                      <span className="dev-prompts__card-rating-label">Quality:</span>
                                      <div className="dev-prompts__star-rating">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <button
                                            key={star}
                                            className={`dev-prompts__star-btn ${prompt.rating >= star ? 'active' : ''}`}
                                            onClick={() => handleSetRating(prompt, prompt.rating === star ? 0 : star)}
                                            title={prompt.rating === star ? 'Clear rating' : `Rate ${star} star${star > 1 ? 's' : ''}`}
                                          >
                                            <Star size={16} fill={prompt.rating >= star ? 'currentColor' : 'none'} />
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Similar Prompts - Semantic Similarity via Embeddings */}
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <SimilarPrompts 
                                        promptId={prompt._id}
                                        onSelectPrompt={(similarPrompt) => {
                                          // Expand the similar prompt's card
                                          setExpandedIds(prev => {
                                            const newSet = new Set(prev);
                                            newSet.add(similarPrompt._id);
                                            return newSet;
                                          });
                                          // Scroll to the prompt
                                          const element = document.querySelector(`[data-prompt-id="${similarPrompt._id}"]`);
                                          if (element) {
                                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                          }
                                        }}
                                      />
                                    </div>

                                    {/* Card Footer */}
                                    <div className="dev-prompts__card-footer">
                                      <div className="dev-prompts__card-stats">
                                        <span title="Times used">
                                          <Copy size={12} />
                                          {prompt.useCount || 0}
                                        </span>
                                        <span title="Version">
                                          <Tag size={12} />
                                          v{prompt.currentVersion || 1}
                                        </span>
                                        <span title="Created">
                                          <Calendar size={12} />
                                          {formatDate(prompt.createdAt)}
                                        </span>
                                        <span title="Last updated">
                                          <Clock size={12} />
                                          {formatDate(prompt.updatedAt)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }}
                        </Draggable>
                      );
                    })
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </main>
      </div>

      {/* Create/Edit Modal */}
      <PromptModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingPrompt(null);
        }}
        onSave={handleSave}
        prompt={editingPrompt}
        categories={CATEGORIES}
        agents={AGENTS}
      />

      {/* ===========================================
          Prompt Revision Modal - Quick prompt iteration
      =========================================== */}
      {showRevisionModal && revisionPrompt && (
        <PromptRevisionModal
          prompt={revisionPrompt}
          onSave={handleRevisionSave}
          onClose={() => {
            setShowRevisionModal(false);
            setRevisionPrompt(null);
          }}
        />
      )}

      {/* Version History Modal */}
      {showVersions && versionPrompt && (
        <div className="dev-prompts__modal-overlay" onClick={() => setShowVersions(false)}>
          <div className="dev-prompts__versions-modal" onClick={e => e.stopPropagation()}>
            <div className="dev-prompts__versions-header">
              <h3>
                <History size={18} />
                Version History
              </h3>
              <p>"{versionPrompt.title}"</p>
              <button className="dev-prompts__close-btn" onClick={() => setShowVersions(false)}>
                ×
              </button>
            </div>
            
            <div className="dev-prompts__versions-list">
              {versions.map((version, index) => (
                <div 
                  key={version.version} 
                  className={`dev-prompts__version ${index === 0 ? 'current' : ''}`}
                >
                  <div className="dev-prompts__version-header">
                    <span className="dev-prompts__version-number">
                      v{version.version}
                      {index === 0 && <span className="dev-prompts__version-current">Current</span>}
                    </span>
                    <span className="dev-prompts__version-date">
                      {formatDate(version.createdAt)}
                    </span>
                  </div>
                  {version.changelog && (
                    <p className="dev-prompts__version-changelog">{version.changelog}</p>
                  )}
                  <code className="dev-prompts__version-content">
                    {truncate(version.content, 300)}
                  </code>
                  {index !== 0 && (
                    <button 
                      className="dev-prompts__version-restore"
                      onClick={() => handleRestoreVersion(version)}
                    >
                      Restore This Version
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===========================================
          Mobile Filter Sheet - Bottom sheet for mobile filtering
          Only visible on screens < 900px
      =========================================== */}
      
      {/* Mobile Filter Trigger Button */}
      <button 
        className="mobile-filter-trigger"
        onClick={() => setMobileFilterOpen(true)}
      >
        <SlidersHorizontal size={16} />
        Filters
        {getActiveFilterCount() > 0 && (
          <span className="mobile-filter-trigger__badge">{getActiveFilterCount()}</span>
        )}
      </button>

      {/* Mobile Filter Backdrop */}
      <div 
        className={`mobile-filter-backdrop ${mobileFilterOpen ? 'visible' : ''}`}
        onClick={() => setMobileFilterOpen(false)}
      />

      {/* Mobile Filter Sheet */}
      <div className={`mobile-filter-sheet ${mobileFilterOpen ? 'open' : ''}`}>
        <div className="mobile-filter-sheet__header">
          <h3>
            <Filter size={16} />
            Filter Prompts
          </h3>
          <button 
            className="mobile-filter-sheet__close"
            onClick={() => setMobileFilterOpen(false)}
          >
            <X size={18} />
          </button>
        </div>

        <div className="mobile-filter-sheet__content">
          {/* Quick Filters */}
          <div className="mobile-filter-sheet__section">
            <h4>Quick Filters</h4>
            <div className="mobile-filter-sheet__options">
              <button 
                className={`mobile-filter-sheet__option ${showFavoritesOnly ? 'active' : ''}`}
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              >
                <Star size={14} />
                Favorites
              </button>
              <button 
                className={`mobile-filter-sheet__option ${visibilityFilter === 'mine' ? 'active' : ''}`}
                onClick={() => setVisibilityFilter(visibilityFilter === 'mine' ? 'all' : 'mine')}
              >
                <User size={14} />
                My Prompts
              </button>
              <button 
                className={`mobile-filter-sheet__option ${visibilityFilter === 'public' ? 'active' : ''}`}
                onClick={() => setVisibilityFilter(visibilityFilter === 'public' ? 'all' : 'public')}
              >
                <Globe size={14} />
                Public
              </button>
            </div>
          </div>

          {/* Rating Filter */}
          <div className="mobile-filter-sheet__section">
            <h4>Minimum Rating</h4>
            <div className="mobile-filter-sheet__options">
              {[0, 1, 2, 3, 4, 5].map((r) => (
                <button
                  key={r}
                  className={`mobile-filter-sheet__option ${minRating === r ? 'active' : ''}`}
                  onClick={() => setMinRating(r)}
                >
                  {r === 0 ? 'Any' : `${r}+`}
                  {r > 0 && <Star size={12} fill="currentColor" />}
                </button>
              ))}
            </div>
          </div>

          {/* Outcomes */}
          {aiTagCategories.outcomes.length > 0 && (
            <div className="mobile-filter-sheet__section">
              <h4>
                <Sparkles size={12} style={{ color: '#ec4899' }} />
                Outcomes ({aiTagCategories.outcomes.length})
              </h4>
              <div className="mobile-filter-sheet__list">
                {aiTagCategories.outcomes.slice(0, 10).map(({ tag, count }) => (
                  <button
                    key={tag}
                    className={`mobile-filter-sheet__list-item ${selectedOutcome === tag ? 'active' : ''}`}
                    onClick={() => setSelectedOutcome(selectedOutcome === tag ? null : tag)}
                  >
                    <Sparkles size={12} />
                    <span>{tag}</span>
                    <span className="mobile-filter-sheet__count">{count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Patterns */}
          {aiTagCategories.patterns.length > 0 && (
            <div className="mobile-filter-sheet__section">
              <h4>
                <Brain size={12} style={{ color: '#a855f7' }} />
                Patterns ({aiTagCategories.patterns.length})
              </h4>
              <div className="mobile-filter-sheet__list">
                {aiTagCategories.patterns.slice(0, 10).map(({ tag, count }) => (
                  <button
                    key={tag}
                    className={`mobile-filter-sheet__list-item ${selectedPattern === tag ? 'active' : ''}`}
                    onClick={() => setSelectedPattern(selectedPattern === tag ? null : tag)}
                  >
                    <Brain size={12} />
                    <span>{tag}</span>
                    <span className="mobile-filter-sheet__count">{count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Technologies */}
          {aiTagCategories.technologies.length > 0 && (
            <div className="mobile-filter-sheet__section">
              <h4>
                <Zap size={12} style={{ color: '#00d4ff' }} />
                Technologies ({aiTagCategories.technologies.length})
              </h4>
              <div className="mobile-filter-sheet__list">
                {aiTagCategories.technologies.slice(0, 10).map(({ tag, count }) => (
                  <button
                    key={tag}
                    className={`mobile-filter-sheet__list-item ${selectedTech === tag ? 'active' : ''}`}
                    onClick={() => setSelectedTech(selectedTech === tag ? null : tag)}
                  >
                    <Zap size={12} />
                    <span>{tag}</span>
                    <span className="mobile-filter-sheet__count">{count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mobile-filter-sheet__actions">
          <button 
            className="mobile-filter-sheet__clear"
            onClick={clearAllFilters}
          >
            Clear All
          </button>
          <button 
            className="mobile-filter-sheet__apply"
            onClick={() => setMobileFilterOpen(false)}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default DevPrompts;
